// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as UserCommands from '@app/features/user/commands/UserCommands';
import {AccountTabContent as AccountTab} from '@app/features/user/components/modals/tabs/account_security_tab/AccountTab';
import {DangerZoneTabContent as DangerZoneTab} from '@app/features/user/components/modals/tabs/account_security_tab/DangerZoneTab';
import styles from '@app/features/user/components/modals/tabs/account_security_tab/Inline.module.css';
import {SecurityTabContent as SecurityTab} from '@app/features/user/components/modals/tabs/account_security_tab/SecurityTab';
import Users from '@app/features/user/state/Users';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useState} from 'react';

const logger = new Logger('AccountSecurityInlineTab');

export const AccountSecurityInlineTab = observer(() => {
	const {t} = useLingui();
	const user = Users.currentUser;
	const [showMaskedEmail, setShowMaskedEmail] = useState(false);
	const [passkeys, setPasskeys] = useState<Array<UserCommands.WebAuthnCredential>>([]);
	const [loadingPasskeys, setLoadingPasskeys] = useState(false);
	const loadPasskeys = useCallback(async () => {
		if (!user?.isClaimed()) {
			setPasskeys([]);
			return;
		}
		setLoadingPasskeys(true);
		try {
			const credentials = await UserCommands.listWebAuthnCredentials();
			setPasskeys(credentials);
		} catch (error) {
			logger.error('Failed to load passkeys', error);
		} finally {
			setLoadingPasskeys(false);
		}
	}, [user]);
	useEffect(() => {
		if (!user?.isClaimed()) {
			setPasskeys([]);
			return;
		}
		loadPasskeys();
	}, [loadPasskeys, user]);
	if (!user) return null;
	const isClaimed = user.isClaimed();
	return (
		<div className={styles.container}>
			{isClaimed && (
				<SettingsSection id="account" title={t`Account`}>
					<AccountTab
						user={user}
						isClaimed={isClaimed}
						showMaskedEmail={showMaskedEmail}
						setShowMaskedEmail={setShowMaskedEmail}
					/>
				</SettingsSection>
			)}
			{isClaimed && (
				<SettingsSection id="security" title={t`Security`}>
					<SecurityTab
						user={user}
						isClaimed={isClaimed}
						passkeys={passkeys}
						loadingPasskeys={loadingPasskeys}
						loadPasskeys={loadPasskeys}
					/>
				</SettingsSection>
			)}
			<SettingsSection id="danger_zone" title={t`Danger Zone`}>
				<DangerZoneTab user={user} isClaimed={isClaimed} />
			</SettingsSection>
		</div>
	);
});
